// NewAgentForm.jsx — m3-16b Agent Jobs builder (the create/edit form).
//
// Ported from tasks-page.jsx lines 366–553. Replaces the list in place when creating
// or editing an agent job (no modal, no route change — a full-pane swap).
//
// Port rules applied:
//   - design <Icon name=.../> → <ShellIcon name=.../>
//   - design icon name map: arrowLeft→arrow-left (already in Phase 0 icons.jsx)
//   - design's self-injected <style> dropped (CSS lives in agent_shell.scss)
//   - ALL .agb-*/.ag-* class names kept verbatim
//   - preview toggle: reuses build's Toggle component (static on, tabIndex=-1, aria-hidden)
//     so it inherits is-on/ag-switch__knob — NOT the design's ag-switch on/ag-knob
//   - KIND_ICON sourced from triggeredListViewModel (shared kind → icon map)
//
// NAMING (LOCKED per TRIGGERED-SPEC-v2 §0):
//   Builder H1 create: "New agent job"
//   Builder H1 edit:   "Edit agent job"
//   Submit create:     "Create job"
//   Submit edit:       "Save changes"

import React, { useState, useEffect, useRef } from "react"
import ShellIcon from "./icons.jsx"
import {
  KIND_ICON, buildTrigger, buildAgentJobPayload,
  SUPPORTED_SCHED_FREQ, SUPPORTED_SCHED_TIME, SUPPORTED_EVENT_OPT, COND_RULE_MAP, TICKET_RULE_KEYS,
  formatEventDateShort, filterEventsByQuery, formCfgForRawTrigger,
  channelChipOn, toggleChannelScope, hydrateContext, describeContext,
} from "./triggeredListViewModel.js"
// A3: the job-level Model + Effort override control mirrors the chat surface's picker exactly
// (same MODELS/EFFORTS/availableEfforts/DEFAULT_MODEL/DEFAULT_EFFORT — single source of truth).
import { MODELS, DEFAULT_MODEL, DEFAULT_EFFORT, availableEfforts } from "./studioShellViewModel.js"
// DESIGN-PANEL repair — kept as its own import statement (not folded into the block above) so the
// A3 wiring test's exact-match regex on that import list keeps passing untouched.
import { capitalizeEffort } from "./studioShellViewModel.js"
import {
  shouldScore, draftKey, buildRatePayload, normalizeRateResponse, RATING_META, DEBOUNCE_MS,
} from "./instructionQualityViewModel.js"
import { buildSetupPrefill } from "./libraryViewModel.js"
import { postData, patchData, fetchData } from "../utils/api"

// ── Builder data constants (verbatim from tasks-page.jsx §2.2) ─────────────────────────────

// Item #6 (2026-07-28): 4th kind 'chat' — visible ALWAYS, including direct create from the Agent
// Jobs page (charter). Label/description locked by the orchestrator's naming pass; the persisted
// kind string stays 'chat'.
const KIND_META = [
  { id: "schedule", icon: "clock",   title: "On a schedule", desc: "Runs at a set time you choose." },
  { id: "event",    icon: "zap",     title: "On a trigger",  desc: "Runs when something happens in Vesta." },
  { id: "condition",icon: "sliders", title: "On a condition",desc: "Runs whenever a rule becomes true." },
  { id: "chat",     icon: "send",    title: "When I ask",    desc: "Runs only when you ask for it in chat." },
]

// D9: these three lists are DERIVED from triggeredListViewModel's supported-set constants (single
// source of truth, verified against TriggerSpec/CONDITION_RULE_KEYS + docs/AGENT_JOB_TRIGGER_SUPPORT.md)
// so the form can never drift from what buildAgentJobPayload actually accepts. The unsupported
// options (the 3 non-publish event options; the sellout condition) are hidden
// here; buildAgentJobPayload's own checks remain as defense-in-depth if a stale/edited seed value
// somehow makes it through.
const SCHED_FREQ = SUPPORTED_SCHED_FREQ
const SCHED_TIME = SUPPORTED_SCHED_TIME

const EVENT_OPTS = [SUPPORTED_EVENT_OPT]

const COND_OPTS = Object.keys(COND_RULE_MAP)

// 16 icon choices for the icon picker (ICON_CHOICES from tasks-page.jsx)
const ICON_CHOICES = [
  "sparkle", "sliders", "alert", "megaphone", "shield", "chart", "clock", "zap",
  "calendar", "dollar", "repeat", "target", "ticket", "trendUp", "send", "eye",
]

// Example chips — clicking sets action + kind + icon (NOT scope)
const EXAMPLES = [
  { action: "Email me a plain-English recap of spend, tickets and ROAS each week.", kind: "schedule", icon: "chart" },
  { action: "Pause any channel whose ROAS drops too low and let me know before it spends more.", kind: "condition", icon: "shield" },
  { action: "Draft campaigns and list every new event the moment it goes live.", kind: "event", icon: "sparkle" },
]

// A3 — UX mirror of AgentJobs::ModelRouting::RECURRING_KINDS (Rails is authoritative; this is a
// picker guard, not a safety fence). Schedule/condition jobs are tick-driven repeaters that may
// never run top-tier EFFORT (xhigh/max) — so the Effort control hides those tiers for recurring
// kinds. D2 (2026-07-28, Billy's instruction): the MODEL ban is lifted — Opus is selectable on
// every kind; only the effort fence remains. The server-side high-frequency condition cap
// (ModelRouting, sub-30-min intervals) has NO mirror here by design: the form never emits
// eval_interval_seconds (every form condition job takes the 3600s column default), so a UI fence
// for it would be dead code. It bites only the agent tool seam, where Rails 422s.
const RECURRING_KINDS = ["schedule", "condition"]
const TOP_TIER_EFFORTS = ["xhigh", "max"] // mirror of AgentJobs::ModelRouting::TOP_TIER_EFFORTS

// A3 — the registry's job_seed.model_id is a CANONICAL FULL id (e.g. "claude-sonnet-4-6"); the
// Model control's internal state (mirroring studioShellViewModel) is the SHORT id ("sonnet"). This
// resolves a registry default to the short id the picker/availableEfforts expect; unmatched/absent
// falls back to DEFAULT_MODEL.
function shortModelIdFor(fullModelId) {
  const m = MODELS.find((x) => x.model_id === fullModelId)
  return m ? m.id : DEFAULT_MODEL
}

// Channels an agent can be scoped to (local to the builder; differs intentionally from CC.CHANNELS,
// which is a badge-palette map, not a scope vocabulary). These ids MUST mirror Rails
// AgentJob::AGENT_CHANNELS, which is itself a subset of Session::CHANNELS — a chip for a channel
// the Session model rejects makes the job's firing die on validation and vanish. Item 6
// (2026-08-17) removed the "Event Calendar Network" chip for exactly that reason: no calendar
// tools exist service-side, and 'calendars' was never in Session::CHANNELS.
// 2026-10-21 (Craig) — the email chip was removed with the agent's email send tools
// (they only mailed an event's ticket holders). 'email' STAYS in the Rails vocabularies because
// existing jobs store it; an untouched edit of such a job saves its stored scope verbatim (see
// scopeTouched below), so removing the chip never silently rewrites a saved job.
const AGENT_CHANNELS = [
  { id: "meta",      name: "Meta Ads",                solid: "#6A3AFB" },
  { id: "social",    name: "Social Media",            solid: "#34C759" },
]

// ── Static preview toggle (reuses build's Toggle class contract) ───────────────────────────

function StaticToggle() {
  return (
    <button
      type="button"
      className="ag-switch is-on"
      aria-hidden="true"
      tabIndex={-1}
    >
      <span className="ag-switch__knob" />
    </button>
  )
}

// DESIGN-PANEL repair (A4 pass) — the picker's type-ahead filter appears only once there are
// enough events that scrolling to find one is a real problem. formatEventDateShort /
// filterEventsByQuery live in triggeredListViewModel.js (imported above) rather than inline here
// — NewAgentForm.jsx is JSX and can't be imported directly by node:test, so logic that lived only
// here could only ever be covered by a static-source regex scan instead of a real unit test.
const SHOW_FILTER_THRESHOLD = 6

// A4 — the event picker for a ticket-based condition rule (sold_out / pct_sold_below_with_days_left).
// ONE shared render used by BOTH the !libraryMode (COND_OPTS) and libraryMode (rawTrigger) paths, so
// the two entry points behave identically and a headless capture can assert DOM identity across both
// (data-testid="agb-event-picker" is the stable selector both AC-7 and AC-11 key off).
//
// DESIGN-PANEL repair: a visible heading + helper line, role="radiogroup" semantics (each row stays
// a real <button>.agb-opt, now also role="radio" + aria-checked), a "Select an event to continue"
// hint wired via aria-describedby while nothing is chosen, a capped/scrollable list plus a
// type-ahead filter once the company has more than a handful of upcoming events, and friendly
// short dates with ellipsis-truncated overlong names (full name still available via title attr).
function EventPicker({ events, eventId, onSelect, loaded }) {
  const [filter, setFilter] = useState("")
  const showFilter = events.length > SHOW_FILTER_THRESHOLD
  const visibleEvents = showFilter ? filterEventsByQuery(events, filter) : events
  const headingId = "agb-event-picker-heading"
  const requiredHintId = "agb-event-picker-required-hint"
  const noneSelected = eventId == null

  return (
    <div className="agb-picker">
      <div className="agb-picker-head">
        <h3 id={headingId} className="agb-sub-lbl">Which event should Vesta watch?</h3>
        {/* DESIGN-PANEL repair (required-state persistence): a small persistent marker, visible in
            BOTH selected/unselected states — distinct from the transient "Select an event to
            continue" hint below, which disappears once eventId is set. Uses the form's own hint
            styling (.agb-hint) so it reads as a field annotation, not new visual language. */}
        <span className="agb-hint agb-picker-required-marker">Required</span>
      </div>
      <p className="agb-hint">This job watches one specific event.</p>
      {showFilter && (
        <input
          type="text"
          className="agb-in agb-picker-filter"
          placeholder="Filter events by name…"
          aria-label="Filter events by name"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      )}
      <div
        className="agb-opts agb-opts--scroll"
        data-testid="agb-event-picker"
        role="radiogroup"
        aria-required="true"
        aria-labelledby={headingId}
        aria-describedby={noneSelected ? requiredHintId : undefined}
        tabIndex={0}
      >
        {visibleEvents.map((ev) => (
          <button
            key={ev.id}
            type="button"
            role="radio"
            aria-checked={eventId === ev.id}
            className={"agb-opt" + (eventId === ev.id ? " on" : "")}
            title={ev.name}
            onClick={() => onSelect(ev.id)}
          >
            <span className="agb-radio" />
            <span className="agb-opt-txt">
              {ev.name}{ev.start_date ? ` — ${formatEventDateShort(ev.start_date)}` : ""}
            </span>
          </button>
        ))}
        {loaded && events.length === 0 && (
          <p className="agb-hint">No upcoming events to pick from yet.</p>
        )}
        {loaded && events.length > 0 && showFilter && visibleEvents.length === 0 && (
          <p className="agb-hint">{`No events match "${filter}".`}</p>
        )}
      </div>
      {noneSelected && (
        <p id={requiredHintId} className="agb-hint agb-picker-required-hint">
          Select an event to continue
        </p>
      )}
    </div>
  )
}

// ── NewAgentForm ───────────────────────────────────────────────────────────────────────────

// D-T9-d (Task 9): merge the user-entered param_inputs values into the raw trigger's rule_params
// (numeric fields become Numbers). This is what keeps budget-cap-watchdog honest — the cap the user
// types lands in rule_params.cap, never an implied default.
function mergeRawTrigger(rawTrigger, paramInputs, paramValues) {
  if (!paramInputs || !paramInputs.length) return rawTrigger
  const ruleParams = { ...(rawTrigger.rule_params || {}) }
  for (const p of paramInputs) {
    const v = paramValues[p.key]
    if (v !== undefined && String(v).trim() !== "") {
      ruleParams[p.key] = p.type === "number" ? Number(v) : v
    }
  }
  return { ...rawTrigger, rule_params: ruleParams }
}

// Task 6 — the advisory instruction-quality chip, shown in the preview header. Fully fail-open:
// idle/off render NOTHING (no error state — a scorer outage is invisible). `loading` → a neutral
// "Checking…" pulse; `done` → a colored pill that toggles a reasons panel, and (yellow/red with an
// optimized rewrite) an Optimize flow that swaps the action textarea ONLY on explicit user confirm.
function QcChip({ qc, onUseOptimized }) {
  const [open, setOpen] = useState(false)
  const [showOpt, setShowOpt] = useState(false)

  if (qc.status === "loading") {
    return <span className="agb-qc-checking" aria-live="polite">Checking…</span>
  }
  if (qc.status !== "done") return null // idle / off → invisible (fail-open)

  const meta = RATING_META[qc.rating] || { label: "" }
  const canOptimize = (qc.rating === "yellow" || qc.rating === "red") && !!qc.optimized

  return (
    <div className="agb-qc-wrap">
      <button
        type="button"
        className={`agb-qc agb-qc--${qc.rating}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="agb-qc-dot" />
        {meta.label}
      </button>
      {open && (
        <div className="agb-qc-panel">
          {!showOpt ? (
            <>
              <ul className="agb-qc-reasons">
                {qc.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
              {canOptimize && (
                <button type="button" className="ag-car-btn agb-qc-optimize" onClick={() => setShowOpt(true)}>
                  <ShellIcon name="sparkle" size={13} />
                  Optimize
                </button>
              )}
            </>
          ) : (
            <>
              <pre className="agb-qc-optimized">{qc.optimized}</pre>
              <div className="agb-qc-actions">
                <button
                  type="button"
                  className="pgs-approve"
                  onClick={() => { onUseOptimized(qc.optimized); setShowOpt(false); setOpen(false) }}
                >
                  Use this version
                </button>
                <button type="button" className="ag-car-btn" onClick={() => setShowOpt(false)}>
                  Keep mine
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default function NewAgentForm({ seed, mode, onCancel, onCreate, companySlug }) {
  const isEdit = mode === "edit"
  const s0 = seed || {}
  const seedKind = s0.kind || "schedule"

  // Item #6 identical-UI rework (2026-07-28): a seed carrying a verbatim `rawTrigger` no longer
  // hides the kind selector + config panels — the FULL form always renders, identical to direct
  // create. The seed still controls SUBMIT derivation via formCfgForRawTrigger:
  //   - libraryCfg non-null (EXPRESSIBLE): panels render interactive, pre-selected to the exact
  //     grammar equivalent of the raw spec. While PRISTINE (triggerDirty false) submit passes
  //     rawTrigger verbatim; the first touch of kind/freq/time/eventOpt/condOpt flips to normal
  //     cfg derivation (which deep-equals the raw spec until the user picks something different).
  //   - libraryCfg null (INEXPRESSIBLE — off-list freq/time such as a monthly cron on a day other
  //     than the 1st/15th, or an unmapped rule_key):
  //     the kind tiles + trigger panel render READ-ONLY (disabled controls + the accurate
  //     trigger_label) so touching is impossible and submit is ALWAYS the verbatim passthrough —
  //     no silent nearest-neighbor mapping can occur. Name/action/icon/scope/model stay editable,
  //     plus any param_inputs the job declares (D15: the cap is typed, never implied).
  const libraryMode = !!s0.rawTrigger
  const paramInputs = s0.paramInputs || []
  const libraryCfg = libraryMode ? formCfgForRawTrigger(seedKind, s0.rawTrigger) : null
  const lockedTrigger = libraryMode && !libraryCfg

  // A12 — s0.setupGaps IS the verbatim computeSetupGaps output ({known, gaps}) fetched server-side
  // (AgentJobs::SetupGaps -> POST /setup-gaps); buildSetupPrefill renders it PRESENTATION-ONLY (no
  // gap computation happens here — see libraryViewModel.js). A param_input pre-fills from it only
  // when it names the A1 question id it corresponds to via `p.qid` (an A8/A9 addition this form
  // consumes, never guesses); absent that link today, this degrades to the unchanged empty default.
  const setupPrefill = buildSetupPrefill(s0.setupGaps)

  // schedule triggers read like "Every day · 8:00 AM CT" — split back into freq + time parts
  const schedParts = (s0.trigger || "").split("·").map((x) => x.trim())

  // surface the seed's own trigger as a selectable option when it isn't already
  // one of the canned choices (so editing keeps the exact wording)
  const eventOpts =
    seedKind === "event" && s0.trigger && !EVENT_OPTS.includes(s0.trigger)
      ? [s0.trigger, ...EVENT_OPTS]
      : EVENT_OPTS
  const condOpts =
    seedKind === "condition" && s0.trigger && !COND_OPTS.includes(s0.trigger)
      ? [s0.trigger, ...COND_OPTS]
      : COND_OPTS

  const [action, setAction]       = useState(s0.action || "")
  // NEXT Item 4 — the NEGOTIATED context ("Specifics"). The stored value is a jsonb HASH; this field
  // is a plain string textarea, so hydrateContext decides what the form is allowed to own:
  //   isNotesOnly false  -> an AGENT-authored structured context: rendered READ-ONLY, key omitted
  //                         from the PATCH, so the server's key-presence guard preserves it.
  //   hasContextKey false-> the seed carries no `context` key at all (an un-wired serializer). In
  //                         EDIT mode the textarea renders DISABLED — the form must never accept
  //                         typing it would silently discard on save. CREATE mode is unaffected
  //                         (the absence of s0 is expected there and the field stays enabled).
  const { hasContextKey, isNotesOnly, initialContext } = hydrateContext(seed)
  const [context, setContext]     = useState(initialContext)
  const contextDisabled = isEdit && !hasContextKey
  const [kind, setKind]           = useState(seedKind)
  // Item #6: an EXPRESSIBLE library seed pre-selects its panels to the exact grammar equivalent of
  // the raw spec (libraryCfg), so what the user sees is what a pristine submit passes through.
  const [freq, setFreq]           = useState(
    libraryCfg && libraryCfg.freq
      ? libraryCfg.freq
      : (seedKind === "schedule" && SCHED_FREQ.includes(schedParts[0]) ? schedParts[0] : SCHED_FREQ[0])
  )
  const [time, setTime]           = useState(
    libraryCfg && libraryCfg.time
      ? libraryCfg.time
      : (seedKind === "schedule" && SCHED_TIME.includes(schedParts[1]) ? schedParts[1] : SCHED_TIME[0])
  )
  const [eventOpt, setEventOpt]   = useState(
    libraryCfg && libraryCfg.event
      ? libraryCfg.event
      : (seedKind === "event" && s0.trigger ? s0.trigger : EVENT_OPTS[0])
  )
  const [condOpt, setCondOpt]     = useState(
    libraryCfg && libraryCfg.cond
      ? libraryCfg.cond
      : (seedKind === "condition" && s0.trigger ? s0.trigger : COND_OPTS[0])
  )
  // Item #6 EXPLICIT dirty-tracking: true the moment the user changes ANY of kind/freq/time/
  // eventOpt/condOpt. rawTrigger has unconditional TOP precedence inside buildAgentJobPayload, so
  // passing it after form state diverged would POST e.g. kind:'condition' with a cron spec (422)
  // or silently discard panel edits — dirty ⇒ submit swaps to normal cfg derivation. On a
  // lockedTrigger seed every trigger control is disabled, so this can never flip there.
  const [triggerDirty, setTriggerDirty] = useState(false)
  // scope seed filters to valid AGENT_CHANNELS ids
  const [scope, setScope]         = useState(
    (s0.scope || []).filter((id) => AGENT_CHANNELS.some((c) => c.id === id))
  )
  // An edit whose chips were never clicked saves the job's STORED scope verbatim — a scope that
  // carries a retired id (e.g. 'email') must not be rewritten by the chip filter above.
  const [scopeTouched, setScopeTouched] = useState(false)
  const [name, setName]           = useState(s0.name || "")
  const [icon, setIcon]           = useState(s0.icon || "sparkle")
  // A3 — Model + Effort override control. In library mode, pre-populated from the template's
  // registry default (s0.modelId/s0.effort, threaded through launchDescriptor); otherwise the same
  // Sonnet/medium cost-guardrail default the chat surface uses.
  const [modelId, setModelId]     = useState(
    libraryMode && s0.modelId ? shortModelIdFor(s0.modelId) : DEFAULT_MODEL
  )
  const [effort, setEffort]       = useState(
    libraryMode && s0.effort ? s0.effort : DEFAULT_EFFORT
  )
  const [iconOpen, setIconOpen]   = useState(false)
  const [busy, setBusy]           = useState(false)
  const [formError, setFormError] = useState(null)
  // D-T9-d: param_inputs values (numeric fields default-prefilled; required when no default).
  const [paramValues, setParamValues] = useState(() => {
    const init = {}
    for (const p of paramInputs) {
      if (p.default !== undefined) {
        init[p.key] = String(p.default)
        continue
      }
      // A12: pre-fill from the server-computed setup-gaps result when this param_input names its
      // A1 question id (p.qid). A gap qid is simply absent from setupPrefill, so it falls through
      // to the unchanged empty string — same as today when no setupGaps was ever fetched.
      const known = p.qid ? setupPrefill[p.qid] : undefined
      init[p.key] = known !== undefined ? String(known) : ""
    }
    return init
  })
  // A4 — the event picker's selection. Pre-selected from the seed's bound event on edit (round-trip
  // via apiJobToTask -> buildRow -> seed.eventId); starts unset for a fresh create/library launch.
  const [eventId, setEventId] = useState(s0.eventId != null ? s0.eventId : null)
  const [selectableEvents, setSelectableEvents] = useState([])
  const [eventsLoaded, setEventsLoaded] = useState(false)

  // A4 — populate the picker from this company's upcoming events (Api::Agent::AgentJobsController#
  // selectable_events). Fetched once per mount; a fetch failure fails open to an empty list (never
  // blocks the rest of the form — the picker just shows "no upcoming events" and submit stays gated).
  useEffect(() => {
    let cancelled = false
    fetchData(`api/agent/companies/${companySlug}/selectable_events`)
      .then((rows) => { if (!cancelled) setSelectableEvents(Array.isArray(rows) ? rows : []) })
      .catch(() => { if (!cancelled) setSelectableEvents([]) })
      .finally(() => { if (!cancelled) setEventsLoaded(true) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug])

  const cfg = { freq, time, event: eventOpt, cond: condOpt }
  // Item #6: the submit-side switch between verbatim passthrough and grammar derivation. Pristine
  // library seed ⇒ raw spec verbatim (and the seed's own trigger label); touched ⇒ derive from the
  // form state — the SAME rule the preview label and the payload below both follow.
  const usesRawTrigger = libraryMode && !triggerDirty
  const trigger = usesRawTrigger ? (s0.triggerLabel || "") : buildTrigger(kind, cfg)

  // A3/D2 — every model (incl. Opus) is selectable for every kind; recurring kinds instead fence
  // the EFFORT lineup (xhigh/max hidden — mirrors the retained server fence). Effort options stay
  // gated per-model (mirrors studioShellViewModel: Haiku -> [] -> control removed).
  const modelOptions = MODELS
  const modelEfforts = availableEfforts(modelId).filter(
    (ef) => !(RECURRING_KINDS.includes(kind) && TOP_TIER_EFFORTS.includes(ef))
  )
  // The effort the form actually SUBMITS — always the one the <select> displays. When the stored
  // `effort` state falls outside the fenced lineup (e.g. Opus/xhigh chosen, then kind flipped to a
  // recurring one), the select already displays modelEfforts[0]; submitting the stale state instead
  // would 422 server-side while the UI shows a legal value. undefined for Haiku (no efforts) —
  // buildAgentJobPayload omits effort for effortless models anyway.
  const effectiveEffort = modelEfforts.includes(effort) ? effort : modelEfforts[0]

  // DESIGN-PANEL repair — refs backing the Model/Effort focus-preservation guard below. `effort`
  // itself is never reset by a model change (only its select's DOM presence toggles on
  // modelEfforts.length), so a prior Sonnet/Opus effort choice comes back exactly as left the
  // moment the user switches back to an effort-bearing model — no extra "restore" bookkeeping
  // needed.
  const modelSelectRef = useRef(null)
  const effortSelectRef = useRef(null)
  const effortHadFocusRef = useRef(false)

  // WCAG 2.4.3 — if the Effort control is focused at the instant the Model changes to one with no
  // effort tiers (Haiku), its <select> is about to unmount from the DOM. Read document.activeElement
  // BEFORE setModelId schedules the re-render (still accurate at this point) and, if it was the
  // Effort select, hand focus to Model rather than letting it silently fall back to <body>.
  const onModelChange = (e) => {
    const nextModelId = e.target.value
    const losingEffort = availableEfforts(nextModelId).length === 0
    const effortWasFocused =
      typeof document !== "undefined" && document.activeElement === effortSelectRef.current
    setModelId(nextModelId)
    if (losingEffort && effortWasFocused) {
      const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (fn) => setTimeout(fn, 0)
      raf(() => { if (modelSelectRef.current) modelSelectRef.current.focus() })
    }
  }

  // Item #6 taste round 1 — one chip grammar (checked = allowed). An empty scope is the implicit
  // all-on state ("Leave all on to let it work across everything"), so a click from there
  // materializes the explicit complement; a toggle landing on the complete set normalizes back to
  // [] so an untouched-equivalent selection persists exactly as the seed stored it.
  const toggleScope = (id) => {
    setScopeTouched(true)
    setScope((s) => toggleChannelScope(s, id, AGENT_CHANNELS.map((c) => c.id)))
  }

  const applyExample = (ex) => {
    setAction(ex.action)
    setIcon(ex.icon)
    // Item #6: examples change kind, so they follow the SAME dirty rule as the kind tiles. On a
    // lockedTrigger seed the trigger is immutable by contract ("timing is fixed") — the example
    // still applies its action/icon but may NOT re-kind the job or flip the passthrough.
    if (!lockedTrigger) {
      setTriggerDirty(true)
      setKind(ex.kind)
    }
    // NOTE: applyExample sets action+kind+icon — NOT scope (design tasks-page.jsx:392)
  }

  // D16 (grader-found blocker): an API-sourced condition job's seed `trigger` is a DISPLAY string
  // (describeTriggerSpec's fallback, e.g. "When roas below") — it is NOT a COND_RULE_MAP key, so
  // label-matching cfg.cond against the map would always miss and false-block a title-only edit as
  // "unsupported". As long as the user hasn't touched the condition select away from that seeded
  // value, pass the job's OWN stored rule_key/rule_params straight through (never re-derived from
  // the label) — buildAgentJobPayload's apiCondition escape hatch. The moment the user picks a real
  // COND_OPTS entry, condOpt changes and this falls back to the normal label-match path.
  // (Computed BEFORE paramsValid/valid — A4's ticket-rule gate below reads apiCondition's ruleKey.)
  const seedRuleKey = seedKind === "condition" ? (s0.ruleKey || null) : null
  const seedRuleParams = seedKind === "condition" ? (s0.ruleParams || null) : null
  const apiCondition =
    kind === "condition" && !!seedRuleKey && condOpt === s0.trigger
      ? { ruleKey: seedRuleKey, ruleParams: seedRuleParams }
      : undefined

  // A4 — which rule_key the CURRENT selection resolves to, for the picker's gate. On the pristine
  // rawTrigger path it's the seed's verbatim rawTrigger.rule_key (the picker never re-derives a
  // library trigger — same TOP-precedence rule buildAgentJobPayload follows); once dirty (or with
  // no library seed) it's the API-sourced passthrough's ruleKey (unedited condition select) or the
  // freshly-picked COND_OPTS option's mapped ruleKey. TICKET_RULE_KEYS (sold_out /
  // pct_sold_below_with_days_left) is the single source of truth both paths gate on — an
  // account-level rule (roas_below, spend_cap_exceeded) never matches.
  const condRuleKey = kind === "condition"
    ? (apiCondition ? apiCondition.ruleKey : (COND_RULE_MAP[condOpt] ? COND_RULE_MAP[condOpt].ruleKey : null))
    : null
  const isTicketLibraryTrigger = usesRawTrigger && TICKET_RULE_KEYS.has((s0.rawTrigger || {}).rule_key)
  const isTicketCondition = !usesRawTrigger && kind === "condition" && TICKET_RULE_KEYS.has(condRuleKey)
  const eventPickerVisible = isTicketCondition || isTicketLibraryTrigger

  // D-T9-d: library mode is only valid once every required (default-less) param_input has a value
  // (D15 — budget-cap-watchdog's cap is required, never an implied default).
  const paramInputsValid = !libraryMode || paramInputs.every((p) =>
    p.default !== undefined || String(paramValues[p.key] ?? "").trim().length > 0)
  // A4 — a ticket rule (either path) blocks submit until an event is chosen (AC-8): fail-CLOSED at
  // the form-validity layer (buildAgentJobPayload itself stays fail-open/always-emits — see its
  // header comment — so this is the ONE place that actually disables .agb-create / stops the POST).
  const eventPickerValid = !eventPickerVisible || eventId != null
  const paramsValid = paramInputsValid && eventPickerValid
  const valid = name.trim().length > 0 && action.trim().length > 0 && paramsValid

  // D14: edit mode now really PATCHes kind + trigger_spec too (Api::Agent::AgentJobsController#
  // update permits kind and routes raw trigger_spec through the SAME normalize/validation path
  // #create uses — agent_jobs_controller.rb). Both paths validate/shape the trigger identically
  // (buildAgentJobPayload), so an edited job can't save an unsupported trigger any more than a new
  // one can. After save + reload, the changed trigger renders because the row is re-derived from
  // the serialized response (apiJobToTask), same as create.
  const submit = async () => {
    if (!valid || busy) return

    // Item #6: ONE call site — the pristine/dirty switch lives in the rawTrigger argument. Pristine
    // library seed ⇒ verbatim passthrough (param values merged in, D15); touched (or no library
    // seed) ⇒ null, so buildAgentJobPayload derives from cfg/apiCondition like a direct create.
    const built = buildAgentJobPayload({
      name: name.trim(), kind, icon, action: action.trim(),
      scope: isEdit && !scopeTouched ? (s0.scope || []).slice() : scope.slice(), cfg, apiCondition,
      rawTrigger: usesRawTrigger ? mergeRawTrigger(s0.rawTrigger, paramInputs, paramValues) : null,
      modelId, effort: effectiveEffort, eventId,
      // Item 4: the edit rule is SHAPE-dependent, so the mapper needs the hydration flags, not just
      // the value (see buildAgentJobPayload's contextFields block).
      context, isEdit, hasContextKey, isNotesOnly,
    })
    if (!built.ok) {
      setFormError(built.reason)
      return
    }

    setFormError(null)
    setBusy(true)
    try {
      if (isEdit) {
        const { name: n, action: a, icon: ic, scope: sc, kind: k, trigger_spec: ts } = built.body.agent_job
        const patchBody = { name: n, action: a, icon: ic, scope: sc, kind: k, trigger_spec: ts }
        // Item 4: KEY PRESENCE is the whole contract on this seam — copy the key only when
        // buildAgentJobPayload decided to emit it, never `context: built.body.agent_job.context`
        // (that would send `undefined`, which serializes as a present-but-null key and CLEARS).
        if ("context" in built.body.agent_job) patchBody.context = built.body.agent_job.context
        const updated = await patchData(
          `api/agent/companies/${companySlug}/agent_jobs/${s0.id}`,
          { agent_job: patchBody }
        )
        onCreate(updated) // parent updates the row in place from the serialized response; NO review chat
        return
      }
      const created = await postData(`api/agent/companies/${companySlug}/agent_jobs`, built.body)
      onCreate(created) // parent owns list update + review-chat seed; form unmounts on success
    } catch (err) {
      setBusy(false)
      const errors = err && err.response && err.response.data && err.response.data.errors
      setFormError(
        Array.isArray(errors) && errors.length
          ? errors[0]
          : isEdit
            ? "Couldn't save changes right now. Try again in a moment."
            : "Couldn't create this job right now. Try again in a moment."
      )
    }
  }

  // Preview placeholders — design literal "Untitled task" (flagged for Billy in CREATE-FLOW-SPEC §4)
  const previewName   = name.trim() || "Untitled task"
  const previewAction = action.trim() || "Describe what this task should do above."

  // ── Task 6: advisory instruction-quality scoring (debounced, memoized, stale-guarded) ──────────
  // ADVISORY ONLY — the submit path (valid / buildAgentJobPayload / submit) never reads `qc`. A
  // debounce timer waits DEBOUNCE_MS after the last edit, a memo key skips identical drafts, and a
  // request counter drops stale responses. Every failure funnels to `off` (invisible chip).
  const [qc, setQc] = useState({ status: "idle" })
  const qcTimer = useRef(null)
  const qcKeyRef = useRef(null)
  const qcReqRef = useRef(0)

  useEffect(() => {
    const draft = { name, action, kind, scope, cfg, apiCondition, rawTrigger: usesRawTrigger ? s0.rawTrigger : undefined }
    if (qcTimer.current) clearTimeout(qcTimer.current)
    if (!shouldScore(draft)) {
      setQc({ status: "idle" })
      return undefined
    }
    qcTimer.current = setTimeout(async () => {
      const key = draftKey(draft)
      if (key === qcKeyRef.current) return // identical draft already scored — collapse the re-fire
      qcKeyRef.current = key
      const reqId = ++qcReqRef.current
      setQc({ status: "loading" })
      try {
        const json = await postData(`api/agent/companies/${companySlug}/agent_jobs/rate`, buildRatePayload(draft))
        if (reqId !== qcReqRef.current) return // stale response — a newer draft is in flight
        const norm = normalizeRateResponse(json)
        setQc(norm.off ? { status: "off" } : { status: "done", rating: norm.rating, reasons: norm.reasons, optimized: norm.optimized })
      } catch {
        if (reqId !== qcReqRef.current) return
        setQc({ status: "off" }) // fail-open — a scorer outage never surfaces
      }
    }, DEBOUNCE_MS)
    return () => { if (qcTimer.current) clearTimeout(qcTimer.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, action, kind, freq, time, eventOpt, condOpt, scope])

  return (
    <div className="ag">
      <div className="ag-wrap agb">
        {/* Header */}
        <div className="agb-top">
          <button
            type="button"
            className="agb-back"
            onClick={onCancel}
            aria-label="Back to agent jobs"
          >
            <ShellIcon name="arrow-left" size={18} />
          </button>
          <div>
            <h1 className="ag-title">
              {isEdit ? "Edit agent job" : "New agent job"}
            </h1>
            <p className="ag-sub" style={{ marginTop: 4 }}>
              {isEdit
                ? "Adjust how Vesta runs this job, then save your changes."
                : "Set up a job for Vesta to run on its own."}
            </p>
          </div>
        </div>

        <div className="agb-form">
          {/* Section 1 — Task Name */}
          <section className="agb-sec">
            <div className="agb-lbl">
              <span className="n">1</span>Task Name
            </div>
            <div className="agb-name-row">
              <input
                className="agb-in"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Slow-seller rescue"
                maxLength={48}
              />
              <button
                type="button"
                className="agb-iconbtn"
                onClick={() => setIconOpen((v) => !v)}
                title="Choose an icon"
                aria-label="Choose an icon"
              >
                <ShellIcon name={icon} size={20} />
              </button>
            </div>
            {iconOpen && (
              <div className="agb-icons">
                {ICON_CHOICES.map((ic) => (
                  <button
                    key={ic}
                    type="button"
                    className={"agb-ic" + (icon === ic ? " on" : "")}
                    onClick={() => { setIcon(ic); setIconOpen(false) }}
                    title={ic}
                  >
                    <ShellIcon name={ic} size={18} />
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* Section 2 — What should this task do? */}
          <section className="agb-sec">
            <div className="agb-lbl">
              <span className="n">2</span>What should this task do?
            </div>
            <textarea
              className="agb-ta"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="e.g. Boost any event that's selling slowly and widen its audience."
            />
            {/* Item 4 — the negotiated context. Optional, never blocks submit (`valid` is untouched). */}
            <h3 className="agb-sub-lbl agb-ctx-lbl">Specifics (optional)</h3>
            {isNotesOnly ? (
              <textarea
                className="agb-ta agb-ctx-ta"
                value={context}
                disabled={contextDisabled}
                onChange={(e) => setContext(e.target.value)}
                placeholder="e.g. Budget $500/mo · Goal: fill weeknight shows · Don't touch the Saturday campaign."
              />
            ) : (
              <p className="agb-hint agb-ctx-ro">Set up by the agent: {describeContext(s0.context)}</p>
            )}
            {contextDisabled && (
              <p className="agb-hint">This job's specifics can't be edited here — ask the agent in chat.</p>
            )}
            <div className="agb-examples">
              {EXAMPLES.map((ex, i) => (
                <button key={i} type="button" className="agb-ex" onClick={() => applyExample(ex)}>
                  <ShellIcon name="sparkle" size={13} />
                  {ex.action.length > 42 ? ex.action.slice(0, 40).trimEnd() + "…" : ex.action}
                </button>
              ))}
            </div>
          </section>

          {/* Section 3 — When should it run? */}
          <section className="agb-sec">
            <div className="agb-lbl">
              <span className="n">3</span>When should it run?
            </div>
            {/* Item #6: the FULL kind selector renders for EVERY entry point (direct create,
                library seed, quick task) — identical UI, always. On a lockedTrigger seed
                (inexpressible raw spec) the tiles render disabled: the seed's kind stays shown
                as selected, but no touch is possible, so the verbatim passthrough can never be
                silently re-derived. */}
            <div className="agb-kinds">
              {KIND_META.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  className={"agb-kind" + (kind === k.id ? " on" : "")}
                  disabled={lockedTrigger}
                  aria-disabled={lockedTrigger || undefined}
                  onClick={() => { setTriggerDirty(true); setKind(k.id) }}
                >
                  <div className="kh">
                    <span className="ki"><ShellIcon name={k.icon} size={16} /></span>
                    <span className="kt">{k.title}</span>
                  </div>
                  <span className="kd">{k.desc}</span>
                </button>
              ))}
            </div>

            {lockedTrigger ? (
              /* Item #6 read-only trigger panel: this template's trigger can't be rebuilt by the
                 form grammar (off-list freq/time, e.g. monthly on an unlisted day / unmapped rule), so the ACCURATE
                 human description renders in place of interactive controls. */
              <div className="agb-cfg">
                <div className="agb-libtrigger">
                  <div className="agb-trigger-chip" aria-label="Trigger">
                    <ShellIcon name={KIND_ICON[kind] || "clock"} size={14} />
                    <span>{s0.triggerLabel}</span>
                  </div>
                  <p className="agb-hint">
                    This template's timing is fixed — the form can't rebuild this trigger yet.
                    Everything else is yours to edit.
                  </p>
                </div>
              </div>
            ) : (
            <div className="agb-cfg">
              {kind === "schedule" && (
                <div className="agb-inline">
                  <div className="agb-field">
                    <label>Frequency</label>
                    <select
                      className="agb-sel"
                      value={freq}
                      onChange={(e) => { setTriggerDirty(true); setFreq(e.target.value) }}
                    >
                      {SCHED_FREQ.map((f) => <option key={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="agb-field">
                    <label>Time</label>
                    <select
                      className="agb-sel"
                      value={time}
                      onChange={(e) => { setTriggerDirty(true); setTime(e.target.value) }}
                    >
                      {SCHED_TIME.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              )}
              {kind === "event" && (
                <div className="agb-opts">
                  {eventOpts.map((o) => (
                    <button
                      key={o}
                      type="button"
                      className={"agb-opt" + (eventOpt === o ? " on" : "")}
                      onClick={() => { setTriggerDirty(true); setEventOpt(o) }}
                    >
                      <span className="agb-radio" />
                      {o}
                    </button>
                  ))}
                </div>
              )}
              {kind === "condition" && (
                <div className="agb-opts">
                  {condOpts.map((o) => (
                    <button
                      key={o}
                      type="button"
                      className={"agb-opt" + (condOpt === o ? " on" : "")}
                      onClick={() => { setTriggerDirty(true); setCondOpt(o) }}
                    >
                      <span className="agb-radio" />
                      {o}
                    </button>
                  ))}
                </div>
              )}
              {kind === "chat" && (
                /* Item #6: the chat kind has NO trigger config — a short honest explainer stands
                   where the other kinds' controls go. */
                <p className="agb-hint agb-chat-note">
                  Nothing to schedule — this job runs when you ask for it in chat. Mention it by
                  name (or what it does) and Vesta will run it.
                </p>
              )}
            </div>
            )}

            {/* D15/D-T9-d: a library seed's declared param_inputs (e.g. budget-cap-watchdog's
                required cap) render for BOTH the locked and interactive branches — the cap the
                user types lands in rule_params, never an implied default. */}
            {libraryMode && paramInputs.length > 0 && (
              <div className="agb-inline">
                {paramInputs.map((p) => (
                  <div className="agb-field" key={p.key}>
                    <label>{p.label}</label>
                    <input
                      className="agb-in"
                      type={p.type === "number" ? "number" : "text"}
                      value={paramValues[p.key] ?? ""}
                      onChange={(e) => setParamValues((v) => ({ ...v, [p.key]: e.target.value }))}
                      placeholder={p.default !== undefined ? String(p.default) : "Required"}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* A4 — the shared event picker for a ticket-based rule on EITHER path: the pristine
                library passthrough (isTicketLibraryTrigger, e.g. sellout-watch) or the form's own
                condition selection (isTicketCondition). Hidden for account-level rules — every
                ticket job used to save event_id:null and never fire (A4 GOAL). */}
            {eventPickerVisible && (
              <EventPicker
                events={selectableEvents}
                eventId={eventId}
                onSelect={setEventId}
                loaded={eventsLoaded}
              />
            )}

          </section>

          {/* Section 4 — AI Model & Effort (DESIGN-PANEL repair: promoted OUT of "When should it
              run?" into its own numbered section — a per-job model/effort override reads as a
              scheduling setting when it's buried in the trigger section, not as what it actually
              is. D2: every model incl. Opus is offered for every kind; xhigh/max effort hidden for
              recurring kinds (schedule/condition) via RECURRING_KINDS+TOP_TIER_EFFORTS; the
              Effort <select> is removed entirely for Haiku (mirrors
              studioShellViewModel.availableEfforts/buildSendBody) but the row SLOT stays — a
              one-line hint takes its place so Model never jumps to full width, and the wrapping
              aria-live="polite" region announces the swap to screen readers. */}
          <section className="agb-sec agb-sec--divider">
            <div className="agb-lbl">
              <span className="n">4</span>AI Model &amp; Effort
            </div>
            <div className="agb-inline agb-modeleffort">
              <div className="agb-field">
                <label>AI model</label>
                <select
                  className="agb-sel"
                  ref={modelSelectRef}
                  value={modelId}
                  onChange={onModelChange}
                >
                  {modelOptions.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
              <div className="agb-field agb-field--effort" aria-live="polite">
                <label>Thinking effort</label>
                {modelEfforts.length > 0 && (
                  <select
                    className="agb-sel"
                    ref={effortSelectRef}
                    value={effectiveEffort}
                    onChange={(e) => setEffort(e.target.value)}
                    onFocus={() => { effortHadFocusRef.current = true }}
                    onBlur={() => { effortHadFocusRef.current = false }}
                  >
                    {modelEfforts.map((ef) => <option key={ef} value={ef}>{capitalizeEffort(ef)}</option>)}
                  </select>
                )}
                {modelEfforts.length === 0 && (
                  <p className="agb-hint agb-effort-hint">Haiku runs at a single effort level</p>
                )}
              </div>
            </div>
            <p className="agb-hint">Sonnet is more thorough; Haiku is faster and lighter.</p>
            {/* A3 effort guidance nit: a parallel one-line hint for the Effort control, matching
                the Model helper directly above it — same .agb-hint styling, same tone. Taste
                round 2 (2026-07-28): hidden when the Effort control itself is hidden (Haiku) —
                explaining a control that isn't on screen reads as a leftover. */}
            {modelEfforts.length > 0 && (
              <p className="agb-hint">Higher effort means more careful work; lower is quicker.</p>
            )}
          </section>

          {/* Section 5 — Channel access */}
          <section className="agb-sec">
            <div className="agb-lbl">
              <span className="n">5</span>
              Which of your channels would you like the task to be able to access?
            </div>
            <p className="agb-hint">
              Choose where this task is allowed to act. Leave all on to let it work across everything.
            </p>
            <div className="agb-chips">
              {AGENT_CHANNELS.map((c) => {
                // taste round 1: empty scope = implicit all-on → every chip renders CHECKED
                // (checked always means allowed — same grammar as a library seed's explicit scope)
                const on = channelChipOn(scope, c.id)
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={"agb-chip" + (on ? " on" : "")}
                    onClick={() => toggleScope(c.id)}
                    style={on ? { color: c.solid } : undefined}
                  >
                    <span className="sw" style={{ background: c.solid }} />
                    <span style={{ color: "var(--vs-fg-1)" }}>{c.name}</span>
                    <span className="ck"><ShellIcon name="check" size={15} /></span>
                  </button>
                )
              })}
            </div>
          </section>

          {/* Live preview */}
          <div className="agb-preview">
            <div className="agb-prev-head">
              <div className="agb-prev-cap">Preview</div>
              <QcChip
                key={qc.status === "done" ? `${qc.rating}:${qc.optimized || ""}` : qc.status}
                qc={qc}
                onUseOptimized={(text) => setAction(text)}
              />
            </div>
            <div className="ag-row">
              <span className="ag-glyph">
                <ShellIcon name={icon} size={19} />
              </span>
              <div className="ag-main">
                <div className="ag-name">{previewName}</div>
                <p className="ag-desc">{previewAction}</p>
                <div className="ag-when">
                  <ShellIcon name={KIND_ICON[kind] || "clock"} size={12} />
                  {trigger}
                </div>
              </div>
              <StaticToggle />
            </div>
          </div>

          {/* Footer */}
          {formError && (
            <div className="agb-err" role="alert" style={{ fontSize: 12, color: "#B42318", marginBottom: 8 }}>
              {formError}
            </div>
          )}
          <div className="agb-foot">
            <button type="button" className="agb-cancel" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="agb-create"
              disabled={!valid || busy}
              onClick={submit}
            >
              <ShellIcon name="check" size={16} />
              {isEdit ? "Save changes" : "Create job"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
